import React, { useState, useCallback, useMemo, useRef } from 'react';
import { ArrowLeft, Check, X } from '@phosphor-icons/react';
import { getEffectiveEstimatedCost } from '../../../../utils/costCalculations';
import { formatCurrencyShort } from '../../../../utils/formatters';
import modalStyles from '../../../../styles/modals.module.css';
import styles from './ItemConfigWizard.module.css';

const formatCurrency = formatCurrencyShort;
const formatNum = v => (v ? Number(v).toLocaleString('he-IL') : '');

// Filter item_options.options[] entries by the current space.
// Rules:
//   - options with empty/missing allowed_space_ids → show everywhere (backward compat)
//   - options with allowed_space_ids populated → show only if masterSpaceId matches
//   - options already selected are preserved regardless (protects edit/back-nav flows)
//   - no masterSpaceId (virtual spaces like כללי) → no filtering
const filterOptionsBySpace = (options, masterSpaceId, selectedOptions) => {
  if (!Array.isArray(options)) return [];
  if (!masterSpaceId) return options;
  return options.filter(opt => {
    const allowed = opt.allowed_space_ids;
    if (!Array.isArray(allowed) || allowed.length === 0) return true;
    if (allowed.includes(masterSpaceId)) return true;
    const group = opt.group || 'default';
    if (selectedOptions && selectedOptions[group] === opt.name) return true;
    return false;
  });
};

// Wizard steps
const STEPS = {
  SELECT_ITEMS: 'select', // For bundles: select which items to include
  CONFIGURE: 'configure', // Configure each selected item
};

/**
 * ItemConfigWizard - Multi-step modal wizard for configuring items before adding
 *
 * Flow:
 * - For bundles: Step 1 = select items, Step 2 = configure each
 * - For non-bundles with options: Just configure step
 * - For simple items: Should not reach wizard (handled directly)
 *
 * Props:
 * - items: Array of master items that need configuration (from chest)
 * - masterItems: Full array of master items (to look up bundle children)
 * - onAddItem: Callback when items are configured and ready to add
 * - onClose: Callback when wizard is closed
 * - costTypes: Array of cost types for dropdown
 * - spaceId: Current space ID
 */
function ItemConfigWizard({
  items = [],
  masterItems = [],
  onAddItem,
  onClose,
  costTypes = [],
  spaceId,
  projectDefaultOptions = [],
  masterSpaceId,
}) {
  // Current item index (for multiple items from chest)
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  // Current step within the item
  const [step, setStep] = useState(STEPS.SELECT_ITEMS);
  // Selected bundle items (for simple bundles) or choice (for choice bundles)
  const [selectedBundleItems, setSelectedBundleItems] = useState({});
  // Configuration for each item
  const [itemConfigs, setItemConfigs] = useState({});
  // Current config item index (for configure step)
  const [configItemIndex, setConfigItemIndex] = useState(0);
  // Global settings
  const [globalSettings, setGlobalSettings] = useState({
    incl_vat: true,
  });
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Accumulated item submissions — only sent to DB on final "סיום"
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  // IDs of items whose price field has been explicitly expanded by the user.
  // The price input is collapsed by default; clicking "+ הוספת הערכת מחיר" reveals it.
  const [priceExpandedIds, setPriceExpandedIds] = useState(() => new Set());
  // Nested-bundle support: when a choice bundle's picked child is itself a bundle,
  // we swap it in as the active wizard item and remember the outer choice ancestor
  // so handleAddItem can stamp it onto relationships.bundle.parent_bundle.
  const [itemSwaps, setItemSwaps] = useState({});
  const [choiceAncestors, setChoiceAncestors] = useState({});
  // Extra copies of a specific bundle child the user wants to add with
  // different options — keyed by child master_item_id, value is an array of
  // finalized config snapshots that get emitted alongside the base child at
  // collect time.
  const [extraChildConfigs, setExtraChildConfigs] = useState({});
  // Ref for restoring UI state when navigating back
  const restoreStateRef = useRef(null);

  const currentItem = itemSwaps[currentItemIndex] || items[currentItemIndex];
  const isLastItem = currentItemIndex === items.length - 1;
  const totalItems = items.length;

  // Get bundle children with full master item data
  const bundleChildren = useMemo(() => {
    if (!currentItem?.is_bundle || !currentItem?.bundle_items?.items) return [];

    return currentItem.bundle_items.items
      .map(bundleItem => {
        const masterItem = masterItems.find(mi => mi.id === bundleItem.item_id);
        if (!masterItem) return null;
        return {
          ...masterItem,
          bundleConfig: bundleItem, // quantity, display_order, shared, max_dependents
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a.bundleConfig.display_order || 0) - (b.bundleConfig.display_order || 0));
  }, [currentItem, masterItems]);

  const isChoiceBundle = currentItem?.bundle_type === 'choice';
  // const isSimpleBundle = currentItem?.is_bundle && !isChoiceBundle;

  // Get items that need configuration (moved up to avoid temporal dead zone)
  const itemsToConfig = useMemo(() => {
    if (!currentItem) return [];

    if (currentItem.is_bundle) {
      // Get selected bundle items
      return bundleChildren.filter(
        child => selectedBundleItems[child.id] || child.bundleConfig.shared,
      );
    } else {
      // Non-bundle item
      return [currentItem];
    }
  }, [currentItem, bundleChildren, selectedBundleItems]);

  const currentConfigItem = itemsToConfig[configItemIndex];
  const isLastConfigItem = configItemIndex === itemsToConfig.length - 1;

  // Calculate dependencies for current config item (base + option-specific)
  const currentItemDependencies = useMemo(() => {
    if (!currentConfigItem) return [];

    const deps = [];
    const seenIds = new Set();

    // Add base dependencies
    if (currentConfigItem.item_options?.base_dependencies) {
      currentConfigItem.item_options.base_dependencies.forEach(dep => {
        if (!seenIds.has(dep.master_item_id)) {
          const masterItem = masterItems.find(mi => mi.id === dep.master_item_id);
          if (masterItem) {
            deps.push({ ...masterItem, depConfig: dep });
            seenIds.add(dep.master_item_id);
          }
        }
      });
    }

    // Add option-specific dependencies based on selected options
    const selectedOptions = itemConfigs[currentConfigItem.id]?.selected_options || {};
    const visibleOptions = filterOptionsBySpace(
      currentConfigItem.item_options?.options,
      masterSpaceId,
      selectedOptions,
    );
    visibleOptions.forEach(opt => {
      // Check if this option is selected (by group or default)
      const group = opt.group || 'default';
      if (selectedOptions[group] === opt.name && opt.dependencies) {
        opt.dependencies.forEach(dep => {
          if (!seenIds.has(dep.master_item_id)) {
            const masterItem = masterItems.find(mi => mi.id === dep.master_item_id);
            if (masterItem) {
              deps.push({ ...masterItem, depConfig: dep });
              seenIds.add(dep.master_item_id);
            }
          }
        });
      }
    });

    return deps;
  }, [currentConfigItem, masterItems, itemConfigs, masterSpaceId]);

  // Get default option for an item from projectDefaultOptions
  const getDefaultOption = useCallback(
    itemId => {
      return projectDefaultOptions?.find(
        opt =>
          opt.master_item_id === itemId &&
          (opt.master_space_id === null || opt.master_space_id === masterSpaceId),
      );
    },
    [projectDefaultOptions, masterSpaceId],
  );

  // Initialize selected items when item changes (or restore saved state on back navigation)
  React.useEffect(() => {
    if (!currentItem) return;

    // If we have state to restore (from back navigation), use it instead of defaults
    if (restoreStateRef.current) {
      const saved = restoreStateRef.current;
      restoreStateRef.current = null;
      setSelectedBundleItems(saved.selectedBundleItems);
      setItemConfigs(saved.itemConfigs);
      setStep(saved.step);
      setConfigItemIndex(saved.configItemIndex);
      setExtraChildConfigs(saved.extraChildConfigs || {});
      setError(null);
      return;
    }

    // Fresh item — drop any leftover extras from a prior bundle instance
    setExtraChildConfigs({});

    if (currentItem.is_bundle && bundleChildren.length > 0) {
      // For simple bundles, default all non-shared items to selected
      // For choice bundles, pre-select from project defaults if available
      const initial = {};
      if (!isChoiceBundle) {
        bundleChildren.forEach(child => {
          if (!child.bundleConfig.shared) {
            initial[child.id] = true;
          }
        });
      } else {
        // Pre-select choice bundle child from project defaults
        const parentDefault = getDefaultOption(currentItem.id);
        if (parentDefault?.selected_option?.selected_child_id) {
          initial[parentDefault.selected_option.selected_child_id] = true;
        }
      }
      setSelectedBundleItems(initial);
      setStep(STEPS.SELECT_ITEMS);
    } else {
      // Non-bundle item with options - go straight to configure
      setStep(STEPS.CONFIGURE);
    }

    // Pre-fill item configs from projectDefaultOptions
    // Current format: { groupName: optionName, cost?: number, notes?: string }
    // or { selected_child_id: uuid } for choice bundles (handled above)
    const initialConfigs = {};
    const itemsToInit = currentItem.is_bundle ? bundleChildren : [currentItem];
    itemsToInit.forEach(item => {
      const defaultOpt = getDefaultOption(item.id);
      if (defaultOpt?.selected_option) {
        // Strip non-option keys, keep group selections
        // eslint-disable-next-line no-unused-vars
        const { cost, notes, selected_child_id, ...optionSelections } = defaultOpt.selected_option;
        if (Object.keys(optionSelections).length > 0) {
          initialConfigs[item.id] = {
            selected_options: optionSelections,
          };
        }
      }
    });
    setItemConfigs(initialConfigs);
    setConfigItemIndex(0);
    setError(null);
  }, [currentItem, bundleChildren, isChoiceBundle, getDefaultOption]);

  // Toggle bundle item selection
  const toggleBundleItem = useCallback(
    itemId => {
      if (isChoiceBundle) {
        // For choice bundles, only one can be selected
        setSelectedBundleItems({ [itemId]: true });
      } else {
        setSelectedBundleItems(prev => ({
          ...prev,
          [itemId]: !prev[itemId],
        }));
      }
      setError(null);
    },
    [isChoiceBundle],
  );

  // Update config for an item
  const updateItemConfig = useCallback((itemId, updates) => {
    setItemConfigs(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], ...updates },
    }));
  }, []);

  // Get config for current item being configured
  const getCurrentConfig = useCallback(() => {
    if (!currentConfigItem) return {};
    const config = itemConfigs[currentConfigItem.id] || {};
    return {
      quantity: config.quantity ?? currentConfigItem.bundleConfig?.quantity ?? 1,
      cost_override: config.cost_override ?? null,
      selected_options: config.selected_options ?? {},
      selected_options_data: config.selected_options_data ?? {},
      cost_type_id: config.cost_type_id ?? currentConfigItem.cost_type_id ?? null,
    };
  }, [currentConfigItem, itemConfigs]);

  // Validate current step
  const isStepValid = useMemo(() => {
    if (step === STEPS.SELECT_ITEMS) {
      // Must have at least one item selected
      const hasSelection = Object.values(selectedBundleItems).some(v => v);
      return hasSelection;
    }
    if (step === STEPS.CONFIGURE && currentConfigItem) {
      // Validate cost type: required if item has no cost_type_id and estimated_cost > 0
      const config = itemConfigs[currentConfigItem.id] || {};
      const needsCostType = currentConfigItem.estimated_cost > 0 && !currentConfigItem.cost_type_id;
      if (needsCostType && !config.cost_type_id) {
        return false;
      }
    }
    return true;
  }, [step, selectedBundleItems, currentConfigItem, itemConfigs]);

  // Collect all dependencies for all items being configured
  // (Moved before handleNext to avoid temporal dead zone)
  const getAllDependencies = useCallback(() => {
    const allDeps = [];
    const seenIds = new Set();

    itemsToConfig.forEach(item => {
      const config = itemConfigs[item.id] || {};
      const selectedOptions = config.selected_options || {};

      // Add base dependencies
      if (item.item_options?.base_dependencies) {
        item.item_options.base_dependencies.forEach(dep => {
          if (!seenIds.has(dep.master_item_id)) {
            const masterItem = masterItems.find(mi => mi.id === dep.master_item_id);
            if (masterItem) {
              allDeps.push({
                masterItem,
                depConfig: dep,
                parentItemId: item.id,
                parentQuantity: config.quantity ?? item.bundleConfig?.quantity ?? 1,
              });
              seenIds.add(dep.master_item_id);
            }
          }
        });
      }

      // Add option-specific dependencies (only for options visible in this space)
      const visibleOptions = filterOptionsBySpace(
        item.item_options?.options,
        masterSpaceId,
        selectedOptions,
      );
      visibleOptions.forEach(opt => {
        const group = opt.group || 'default';
        if (selectedOptions[group] === opt.name && opt.dependencies) {
          opt.dependencies.forEach(dep => {
            if (!seenIds.has(dep.master_item_id)) {
              const masterItem = masterItems.find(mi => mi.id === dep.master_item_id);
              if (masterItem) {
                allDeps.push({
                  masterItem,
                  depConfig: dep,
                  parentItemId: item.id,
                  parentQuantity: config.quantity ?? item.bundleConfig?.quantity ?? 1,
                });
                seenIds.add(dep.master_item_id);
              }
            }
          });
        }
      });
    });

    return allDeps;
  }, [itemsToConfig, itemConfigs, masterItems, masterSpaceId]);

  // Collect current item's prepared data + snapshot of UI state (no DB write)
  const collectCurrentItem = useCallback(() => {
    const buildEntry = (item, config) => {
      const costTypeOverride =
        config.cost_type_id && config.cost_type_id !== item.cost_type_id
          ? config.cost_type_id
          : null;
      return {
        master_item_id: item.id,
        space_id: spaceId,
        quantity: config.quantity ?? item.bundleConfig?.quantity ?? 1,
        cost_override: config.cost_override ?? null,
        estimated_cost: item.estimated_cost,
        incl_vat: globalSettings.incl_vat,
        selected_option: config.selected_options ?? {},
        selected_option_data: config.selected_options_data ?? {},
        cost_type_id: config.cost_type_id ?? item.cost_type_id ?? null,
        cost_type_override: costTypeOverride,
      };
    };

    // Emit the base config for each child, then any extra copies the user
    // added via "+ הוסף עוד ..." (same master_item_id, different options).
    const itemsData = [];
    itemsToConfig.forEach(item => {
      const baseConfig = itemConfigs[item.id] || {};
      itemsData.push(buildEntry(item, baseConfig));
      const extras = extraChildConfigs[item.id] || [];
      extras.forEach(extraConfig => {
        itemsData.push(buildEntry(item, extraConfig));
      });
    });

    // Add dependency items
    const allDeps = getAllDependencies();
    allDeps.forEach(({ masterItem, depConfig, parentQuantity }) => {
      const depQuantity = depConfig.quantity_type === 'same_as_main' ? parentQuantity : 1;
      itemsData.push({
        master_item_id: masterItem.id,
        space_id: spaceId,
        quantity: depQuantity,
        cost_override: null,
        estimated_cost: masterItem.estimated_cost,
        incl_vat: globalSettings.incl_vat,
        selected_option: depConfig.preset_options || {},
        cost_type_id: masterItem.cost_type_id ?? null,
        is_dependency: true, // Mark as dependency for table grouping
      });
    });

    // Include bundle info if this is a bundle
    const bundleInfo = currentItem.is_bundle
      ? {
          master_item_id: currentItem.id,
          is_bundle: true,
          bundle_type: currentItem.bundle_type || 'simple',
          choice_ancestor: choiceAncestors[currentItemIndex] || null,
        }
      : null;

    return {
      itemsData,
      bundleInfo,
      itemIndex: currentItemIndex,
      // Snapshot of UI state for restoration on back navigation
      savedState: {
        itemConfigs: { ...itemConfigs },
        selectedBundleItems: { ...selectedBundleItems },
        step,
        configItemIndex,
        extraChildConfigs: { ...extraChildConfigs },
      },
    };
  }, [
    itemsToConfig,
    itemConfigs,
    selectedBundleItems,
    step,
    configItemIndex,
    spaceId,
    globalSettings,
    currentItem,
    currentItemIndex,
    getAllDependencies,
    choiceAncestors,
    extraChildConfigs,
  ]);

  // Submit ALL collected items to DB (called only on final "סיום")
  const submitAll = useCallback(async () => {
    setIsSubmitting(true);
    try {
      // Collect current (final) item, then submit everything
      const currentCollection = collectCurrentItem();
      const allSubmissions = [...pendingSubmissions, currentCollection];

      for (const submission of allSubmissions) {
        await onAddItem({ items: submission.itemsData, bundleInfo: submission.bundleInfo });
      }

      onClose();
    } catch (err) {
      setError('אירעה שגיאה בשמירת הפריטים');
    } finally {
      setIsSubmitting(false);
    }
  }, [collectCurrentItem, pendingSubmissions, onAddItem, onClose]);

  // Handle next button
  const handleNext = useCallback(() => {
    if (!isStepValid) {
      if (step === STEPS.SELECT_ITEMS) {
        setError('יש לבחור לפחות פריט אחד');
      } else if (step === STEPS.CONFIGURE) {
        setError('יש לבחור סוג עלות');
      }
      return;
    }

    if (step === STEPS.SELECT_ITEMS) {
      // Nested bundle: if a choice-bundle child is itself a bundle, swap it in
      // as the active wizard item so its children/dependencies get expanded.
      if (isChoiceBundle) {
        const selectedId = Object.keys(selectedBundleItems).find(k => selectedBundleItems[k]);
        const childMaster = selectedId ? masterItems.find(mi => mi.id === selectedId) : null;
        if (childMaster?.is_bundle) {
          setChoiceAncestors(prev => ({
            ...prev,
            [currentItemIndex]: {
              master_item_id: currentItem.id,
              bundle_type: 'choice',
            },
          }));
          setItemSwaps(prev => ({ ...prev, [currentItemIndex]: childMaster }));
          return;
        }
      }
      // Move to configure step
      setStep(STEPS.CONFIGURE);
      setConfigItemIndex(0);
      return;
    }

    // In configure step - move to next config item within this bundle/item
    if (!isLastConfigItem) {
      setConfigItemIndex(prev => prev + 1);
      return;
    }

    // All config items done for this wizard item
    if (isLastItem) {
      // Final item — submit everything to DB
      submitAll();
    } else {
      // Collect this item and advance to next wizard item
      const collected = collectCurrentItem();
      setPendingSubmissions(prev => [...prev, collected]);
      setCurrentItemIndex(prev => prev + 1);
    }
  }, [
    isStepValid,
    step,
    isChoiceBundle,
    selectedBundleItems,
    masterItems,
    currentItem,
    currentItemIndex,
    isLastConfigItem,
    isLastItem,
    submitAll,
    collectCurrentItem,
  ]);

  // Handle cancel with confirmation
  const handleCancel = useCallback(() => {
    const totalPending = pendingSubmissions.length + 1; // +1 for current unsaved item
    const message =
      totalPending > 1
        ? `${totalPending} פריטים לא יתווספו אם תסגור. להמשיך?`
        : 'הפריט הנוכחי לא יתווסף אם תסגור. להמשיך?';

    if (window.confirm(message)) {
      onClose();
    }
  }, [pendingSubmissions.length, onClose]);

  // Handle back button
  const handleBack = useCallback(() => {
    // Within bundle configure: go to previous config item
    if (step === STEPS.CONFIGURE && configItemIndex > 0) {
      setConfigItemIndex(prev => prev - 1);
      return;
    }

    // From bundle configure step back to bundle select step
    if (step === STEPS.CONFIGURE && currentItem?.is_bundle) {
      setStep(STEPS.SELECT_ITEMS);
      return;
    }

    // Un-swap a nested bundle: go back to the outer choice picker
    if (step === STEPS.SELECT_ITEMS && itemSwaps[currentItemIndex]) {
      setItemSwaps(prev => {
        const next = { ...prev };
        delete next[currentItemIndex];
        return next;
      });
      setChoiceAncestors(prev => {
        const next = { ...prev };
        delete next[currentItemIndex];
        return next;
      });
      return;
    }

    // Cross-item: go back to previous "add another" or previous wizard item
    if (pendingSubmissions.length > 0) {
      const lastSubmission = pendingSubmissions[pendingSubmissions.length - 1];
      setPendingSubmissions(prev => prev.slice(0, -1));

      if (lastSubmission.itemIndex === currentItemIndex) {
        // Was an "add another" for the same item — restore that config inline
        const saved = lastSubmission.savedState;
        setItemConfigs(saved.itemConfigs);
        setSelectedBundleItems(saved.selectedBundleItems);
        setStep(saved.step);
        setConfigItemIndex(saved.configItemIndex);
      } else {
        // Was a different wizard item — navigate back to it via ref + index change
        restoreStateRef.current = lastSubmission.savedState;
        setCurrentItemIndex(lastSubmission.itemIndex);
      }
      setError(null);
    }
  }, [step, configItemIndex, currentItem, pendingSubmissions, currentItemIndex, itemSwaps]);

  const canGoBack =
    (step === STEPS.CONFIGURE && configItemIndex > 0) ||
    (step === STEPS.CONFIGURE && currentItem?.is_bundle) ||
    (step === STEPS.SELECT_ITEMS && !!itemSwaps[currentItemIndex]) ||
    pendingSubmissions.length > 0;

  if (!currentItem) return null;

  // Get step title
  const getStepTitle = () => {
    if (step === STEPS.SELECT_ITEMS) {
      return isChoiceBundle ? 'בחר אפשרות' : 'בחר פריטים להוספה';
    }
    return 'הגדרות';
  };

  // Resolve the cost type name for the current config item
  const currentCostTypeName = useMemo(() => {
    const ctId = currentConfigItem?.cost_type_id;
    if (!ctId) return null;
    return costTypes.find(ct => ct.id === ctId)?.name || null;
  }, [currentConfigItem?.cost_type_id, costTypes]);

  // Price field is collapsed by default. It auto-expands if the current item
  // already has a cost_override (back navigation / edit flow) OR if the user
  // explicitly clicked the "+ הוספת הערכת מחיר" button for this item.
  const isCurrentPriceExpanded =
    !!currentConfigItem &&
    (priceExpandedIds.has(currentConfigItem.id) ||
      itemConfigs[currentConfigItem.id]?.cost_override != null);

  return (
    <div className={modalStyles.overlay} onClick={handleCancel}>
      <div className={modalStyles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={modalStyles.header}>
          <button className={modalStyles.closeButton} onClick={handleCancel} aria-label="סגור">
            <X size={20} weight="bold" />
          </button>
          {totalItems > 1 && (
            <div className={styles.progress}>
              פריט {currentItemIndex + 1} מתוך {totalItems}
            </div>
          )}
          {/* For bundle configure step: show child item as main title, bundle as subtitle */}
          {step === STEPS.CONFIGURE && itemsToConfig.length > 1 && currentConfigItem ? (
            <>
              <div className={styles.bundleContext}>
                {currentItem.item_name} · {configItemIndex + 1}/{itemsToConfig.length}
              </div>
              <h2 className={styles.itemName}>{currentConfigItem.item_name}</h2>
            </>
          ) : (
            <h2 className={styles.itemName}>{currentItem.item_name}</h2>
          )}
          <div className={styles.stepTitle}>{getStepTitle()}</div>
        </div>

        {/* Content */}
        <div className={modalStyles.content}>
          {error && <div className={modalStyles.error}>{error}</div>}

          {/* Step 1: Select Items (for bundles) */}
          {step === STEPS.SELECT_ITEMS && (
            <div className={styles.section}>
              {isChoiceBundle ? (
                // Choice bundle - cards
                <div className={styles.choiceGrid}>
                  {bundleChildren.map(child => (
                    <div
                      key={child.id}
                      className={`${styles.choiceCard} ${selectedBundleItems[child.id] ? styles.selected : ''}`}
                      onClick={() => toggleBundleItem(child.id)}
                    >
                      <span className={styles.choiceName}>{child.item_name}</span>
                      {child.estimated_cost > 0 && (
                        <span className={styles.choiceCost}>
                          {formatCurrency(child.estimated_cost)}
                        </span>
                      )}
                      {selectedBundleItems[child.id] && (
                        <div className={styles.checkMark}>
                          <Check size={20} weight="bold" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                // Simple bundle - checkboxes
                <div className={styles.itemsList}>
                  {bundleChildren.map(child => {
                    const isShared = child.bundleConfig.shared;
                    const isSelected = selectedBundleItems[child.id] || isShared;

                    return (
                      <div
                        key={child.id}
                        className={`${styles.itemRow} ${isSelected ? styles.selected : ''} ${isShared ? styles.shared : ''}`}
                        onClick={() => !isShared && toggleBundleItem(child.id)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isShared}
                          onChange={e => {
                            e.stopPropagation();
                            if (!isShared) toggleBundleItem(child.id);
                          }}
                          onClick={e => e.stopPropagation()}
                        />
                        <div className={styles.itemInfo}>
                          <span className={styles.itemName}>{child.item_name}</span>
                          {child.estimated_cost > 0 && (
                            <span className={styles.itemCost}>
                              {formatCurrency(child.estimated_cost)}
                            </span>
                          )}
                        </div>
                        {isShared && <span className={styles.sharedBadge}>משותף</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Configure Items */}
          {step === STEPS.CONFIGURE && currentConfigItem && (
            <>
              {/* Item Options */}
              {currentConfigItem.item_options?.options && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>אפשרויות</h3>
                  {Object.entries(
                    filterOptionsBySpace(
                      currentConfigItem.item_options.options,
                      masterSpaceId,
                      getCurrentConfig().selected_options,
                    ).reduce((acc, opt) => {
                      const group = opt.group || 'default';
                      if (!acc[group]) acc[group] = [];
                      acc[group].push(opt);
                      return acc;
                    }, {}),
                  ).map(([group, options]) => (
                    <div key={group} className={styles.optionField}>
                      <label className={styles.optionLabel}>
                        {group === 'default' ? 'בחר אפשרות' : group}
                      </label>
                      <select
                        className={styles.optionSelect}
                        value={getCurrentConfig().selected_options?.[group] || ''}
                        onChange={e => {
                          const optionName = e.target.value;
                          const optionData = options.find(opt => opt.name === optionName);
                          updateItemConfig(currentConfigItem.id, {
                            selected_options: {
                              ...getCurrentConfig().selected_options,
                              [group]: optionName,
                            },
                            selected_options_data: {
                              ...getCurrentConfig().selected_options_data,
                              [group]: optionData || null,
                            },
                          });
                        }}
                      >
                        <option value="">בחר...</option>
                        {options.map(opt => (
                          <option key={opt.name} value={opt.name}>
                            {opt.name}
                            {opt.cost_modifier ? ` (+${formatCurrency(opt.cost_modifier)})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}

              {/* Quantity and Cost */}
              <div className={styles.section}>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>
                      כמות{currentCostTypeName ? ` (${currentCostTypeName})` : ''}
                    </label>
                    <input
                      type="number"
                      className={styles.fieldInput}
                      min="1"
                      value={getCurrentConfig().quantity}
                      onChange={e =>
                        updateItemConfig(currentConfigItem.id, {
                          quantity: parseInt(e.target.value) || 1,
                        })
                      }
                    />
                  </div>
                  {isCurrentPriceExpanded ? (
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>מחיר (אופציונלי)</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className={styles.fieldInput}
                        value={
                          getCurrentConfig().cost_override
                            ? `₪ ${formatNum(getCurrentConfig().cost_override)}`
                            : ''
                        }
                        onChange={e => {
                          const value = e.target.value.replace(/[^\d]/g, '');
                          updateItemConfig(currentConfigItem.id, {
                            cost_override: value ? parseInt(value) : null,
                          });
                        }}
                        placeholder={
                          formatCurrency(
                            getEffectiveEstimatedCost(
                              currentConfigItem,
                              getCurrentConfig().selected_options,
                            ),
                          ) || '₪'
                        }
                      />
                      <span className={styles.fieldHint}>
                        הערכה שלך. תמחור ספק ניתן להוסיף מאוחר יותר
                      </span>
                    </div>
                  ) : (
                    <div className={styles.field}>
                      <button
                        type="button"
                        className={styles.addPriceButton}
                        onClick={() =>
                          setPriceExpandedIds(prev => {
                            const next = new Set(prev);
                            next.add(currentConfigItem.id);
                            return next;
                          })
                        }
                      >
                        + הוספת הערכת מחיר
                      </button>
                    </div>
                  )}
                </div>

                {/* VAT checkbox - only on first config item, and only when price is expanded */}
                {configItemIndex === 0 && isCurrentPriceExpanded && (
                  <label className={styles.checkboxField}>
                    <input
                      type="checkbox"
                      checked={globalSettings.incl_vat}
                      onChange={e =>
                        setGlobalSettings(prev => ({ ...prev, incl_vat: e.target.checked }))
                      }
                    />
                    <span className={styles.checkboxLabel}>כולל מע&quot;מ</span>
                  </label>
                )}
              </div>

              {/* Dependencies Note */}
              {currentItemDependencies.length > 0 && (
                <div className={styles.dependencyNote}>
                  <span className={styles.dependencyIcon}>ℹ️</span>
                  <span>
                    יתווספו אוטומטית לכתב כמויות:{' '}
                    {currentItemDependencies.map(dep => dep.item_name).join(', ')}
                  </span>
                </div>
              )}

              {/* Cost Type dropdown — only when item has no cost_type_id set */}
              {!currentCostTypeName && costTypes.length > 0 && (
                <div className={styles.section}>
                  <div className={styles.optionField}>
                    <label className={styles.optionLabel}>סוג עלות</label>
                    <select
                      className={styles.optionSelect}
                      value={getCurrentConfig().cost_type_id || ''}
                      onChange={e =>
                        updateItemConfig(currentConfigItem.id, {
                          cost_type_id: e.target.value || null,
                        })
                      }
                    >
                      <option value="">בחר סוג עלות</option>
                      {costTypes.map(ct => (
                        <option key={ct.id} value={ct.id}>
                          {ct.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={modalStyles.footer}>
          {canGoBack ? (
            <button className={modalStyles.cancelButton} onClick={handleBack}>
              חזור
            </button>
          ) : (
            <button className={modalStyles.cancelButton} onClick={handleCancel}>
              ביטול
            </button>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {step === STEPS.CONFIGURE && currentItem?.is_bundle && currentConfigItem && (
              <button
                className={modalStyles.secondaryButton}
                onClick={() => {
                  if (!isStepValid) return;
                  const childId = currentConfigItem.id;
                  const currentConfig = itemConfigs[childId] || {};
                  setExtraChildConfigs(prev => ({
                    ...prev,
                    [childId]: [...(prev[childId] || []), currentConfig],
                  }));
                  setItemConfigs(prev => {
                    const next = { ...prev };
                    delete next[childId];
                    return next;
                  });
                  setPriceExpandedIds(prev => {
                    const next = new Set(prev);
                    next.delete(childId);
                    return next;
                  });
                  setError(null);
                }}
                disabled={!isStepValid || isSubmitting}
                title={`הוסף מופע נוסף של ${currentConfigItem.item_name} עם אפשרויות אחרות`}
              >
                + הוסף עוד {currentConfigItem.item_name}
                {(extraChildConfigs[currentConfigItem.id]?.length || 0) > 0 &&
                  ` (${(extraChildConfigs[currentConfigItem.id]?.length || 0) + 1})`}
              </button>
            )}
            {step === STEPS.CONFIGURE && isLastConfigItem && (
              <button
                className={modalStyles.secondaryButton}
                onClick={() => {
                  if (!isStepValid) return;
                  // Collect current config, then reset form for another of the same item
                  const collected = collectCurrentItem();
                  setPendingSubmissions(prev => [...prev, collected]);
                  // Reset form — clear configs, go back to first step for this item
                  setItemConfigs({});
                  setExtraChildConfigs({});
                  setConfigItemIndex(0);
                  if (currentItem?.is_bundle) {
                    setStep(STEPS.SELECT_ITEMS);
                  }
                  setError(null);
                }}
                disabled={!isStepValid || isSubmitting}
              >
                + הוסף פריט עם אפשרויות אחרות
              </button>
            )}
            <button
              className={modalStyles.primaryButton}
              onClick={handleNext}
              disabled={!isStepValid || isSubmitting}
            >
              {isSubmitting
                ? 'שומר...'
                : step === STEPS.SELECT_ITEMS
                  ? 'הבא'
                  : isLastConfigItem && isLastItem
                    ? 'סיום'
                    : 'הבא'}
              {!isSubmitting && <ArrowLeft size={18} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(ItemConfigWizard);
